import { CONST } from '@common/constants/const.constant';
import { t } from '@common/hooks/use-t.hook';
import { IIdObject } from '@common/interfaces/id-object.interface';
import { consoleService } from '@common/services/console.service';
import { EOrderPaymentStatus, IItemPrice, IOrder, IOrderAddons } from '@src/api/item-generated-api';
import dayjs from 'dayjs';
import { apiHooks } from '../hooks/api-hooks';
import { ICachedItemPriceDto } from './cached-item-price.dto';
import { IDeliveryConfigDto } from './delivery-config.dto';
import {
    createExtendProductCatalogItemDto,
    IExtendedProductCatalogItemDto,
} from './extended-product-catalog-item.dto';
import { IProductCatalogItemDto } from './product-catalog-item.dto';
import { IBoxCustomisationConfigDefaultProduceDto, createDefaultProduceDto } from './box-customisation-default-produce.dto';
import { getDiscountString } from '@src/shared/utils/get-discount-string/get-discount-string.util';
import { getDiscountTitle } from '@src/shared/utils/get-discount-title/get-discount-title.util';

// TODO: do we need this? it's basically just duplication of EOrderPaymentStatus
export type TPaymentStatus = 'paid' | 'pending' | 'payment_failed' | 'cancelled' | 'partial_refund' | 'refunded';
export interface IOrderDto extends IIdObject, Omit<IOrder, 'id' | 'discount' | 'payment_status' | 'chosen_produce'> {
    shippingDate: Date;
    orderDate: Date;
    finishDeliveryDate: Date;
    totalAddonsPrice: number;
    shippingAddressLabel: string;
    billingAddressLabel: string;

    productObj: IExtendedProductCatalogItemDto | undefined;
    priceObj: ICachedItemPriceDto | undefined;
    addonsObj: IExtendedProductCatalogItemDto[];

    discount: string | undefined;
    discountTitle: string | undefined;
    discountTotal: number | undefined;
    paymentStatus: TPaymentStatus | undefined;
    paymentStatusLabel: string | undefined;
    orderDateWithDCCutOfTime_blockedOnUI: Date | undefined;
    chosen_produce: IBoxCustomisationConfigDefaultProduceDto[] | undefined
}

const getPaymentStatus = (paymentStatus: EOrderPaymentStatus | undefined): TPaymentStatus => {
    if (paymentStatus === EOrderPaymentStatus.paid) {
        return 'paid';
    } else if (paymentStatus === EOrderPaymentStatus.pending) {
        return 'pending';
    } else if (paymentStatus === EOrderPaymentStatus.cancelled) {
        return 'cancelled';
    } else if (paymentStatus === EOrderPaymentStatus.partial_refund) {
        return 'partial_refund';
    } else if (paymentStatus === EOrderPaymentStatus.refunded) {
        return 'refunded';
    }
    return EOrderPaymentStatus.payment_failed;
};

const PAYMENT_STATUS_MAP: Record<TPaymentStatus, string> = {
    [EOrderPaymentStatus.paid]: t('Paid'),
    [EOrderPaymentStatus.pending]: t('Pending payment'),
    [EOrderPaymentStatus.cancelled]: t('Cancelled'),
    [EOrderPaymentStatus.payment_failed]: t('Payment failed'),
    [EOrderPaymentStatus.partial_refund]: t('Partially refunded'),
    [EOrderPaymentStatus.refunded]: t('Refunded'),
};

export const createOrderDto = (source: IOrder, deliveryConfig?: IDeliveryConfigDto | undefined): IOrderDto => {
    const allProducts = apiHooks.packHouse.getProductsFromCache();
    const productBoxId = source.product_code || '';

    const product = allProducts?.list.find((f) => f.id === productBoxId) as IProductCatalogItemDto;
    consoleService.assert(productBoxId, 'product not found for order productId=' + productBoxId);
    const productObj = createExtendProductCatalogItemDto({
        product,
        amount: product?.price,
        item_price_id: source.product_id,
        quantity: 1,
    });
    const priceObj = product?.item?.prices.find((price: IItemPrice) => price.id === source.product_id);

    if (!deliveryConfig) {
        const allDeliveryConfigs = apiHooks.packHouse.getDeliveryConfigsFromCache();
        deliveryConfig = allDeliveryConfigs?.list.find((dc: IDeliveryConfigDto) => dc.id === source.dc_id);
    }

    consoleService.assert(
        deliveryConfig,
        'Delivery config not found for order cf_delivery_configuration_id=' + source.dc_id,
    );

    const paymentStatus = getPaymentStatus(source.payment_status);
    const addonsPrice = source.addons?.map((m) => (m.price || 0) * (m.quantity || 0)) || [];

    const allAddons =
        source.addons?.map((original: IOrderAddons) => {
            const product = allProducts?.list.find(
                (addon) => addon.id === original.product_code,
            ) as IProductCatalogItemDto;

            consoleService.assert(product, 'product not found inside order (addon) product_code=' + original.product_code);
            return createExtendProductCatalogItemDto({
                product,
                amount: original.price,
                item_price_id: original.product_code,
                quantity: original.quantity || 0,
            });
        }) || [];
    const chosenProduce = source.chosen_produce?.map((produce) => {
        return createDefaultProduceDto(produce) || [];
    });
    return {
        ...source,
        paymentStatus,
        paymentStatusLabel: PAYMENT_STATUS_MAP[paymentStatus],
        id: source?.order_id || '',
        totalAddonsPrice: addonsPrice.reduce((prev, curr) => prev + curr, 0),
        orderDate: new Date(Math.round((source?.order_date || 0) * 1000)),
        shippingDate: new Date(Math.round((source?.delivery_date || 0) * 1000)),
        shippingAddressLabel: [
            source.shipping_address,
            source.shipping_address2,
            source.shipping_city,
            source.shipping_zip_code,
            source.shipping_country,
        ]
            .filter((f) => f)
            .join(', '),
        billingAddressLabel: [
            source.billing_address,
            source.billing_address2,
            source.billing_city,
            source.billing_zip_code,
            source.billing_country,
        ]
            .filter((f) => f)
            .join(', '),

        productObj,
        priceObj,
        addonsObj: allAddons,
        discount: getDiscountString(source.coupon_info),
        discountTitle: getDiscountTitle(source.coupon_info),
        discountTotal: source.discount,
        finishDeliveryDate: dayjs(new Date(Math.round((source?.delivery_date || 0) * 1000)))
            .add(1, 'day')
            .set('hours', 0)
            .set('minutes', 1)
            .toDate(),

        orderDateWithDCCutOfTime_blockedOnUI: dayjs((source.order_date || 0) * 1000)
            .set('hours', deliveryConfig?.cutOffTime?.getHours() || 0)
            .set('minutes', deliveryConfig?.cutOffTime?.getMinutes() || 0)
            .subtract(CONST.BLOCK_UPCOMING_DELIVERY_BEFORE_CUTOFF_IN_MINUTES, 'minutes')
            .toDate(),
        chosen_produce: chosenProduce,
    };
};
